// "Page not found" for routes the app doesn't know (EN and FR "*" routes, unknown
// service / industry / case-study / city + service slugs). The server answers unknown
// URLs with public/404.html (status 404, see vercel.json); this covers client-side
// navigation and the app-shell routes. Same links as public/404.html.
import React from "react";
import { Link, useLocation } from "react-router-dom";
import SEO from "./SEO";

const TEXT = {
  en: {
    code: "Error 404",
    h1: "Page not found",
    lead: "The page you're looking for doesn't exist or has moved. Try one of the links below, or call us: our phones are answered 24/7.",
    home: ["/", "Go to the homepage"],
    contact: ["/contact", "Contact us"],
    servicesTitle: "Services",
    services: [
      ["/services/security-guards", "Security guards"],
      ["/services/event-security", "Event security"],
      ["/services/patrol-services", "Mobile patrol"],
    ],
    citiesTitle: "Cities",
    cities: [
      ["/montreal", "Montréal"],
      ["/toronto", "Toronto"],
      ["/ottawa", "Ottawa"],
      ["/quebec-city", "Québec City"],
      ["/laval", "Laval"],
      ["/trois-rivieres", "Trois-Rivières"],
      ["/niagara-falls", "Niagara Falls"],
    ],
    callTitle: "Call us, 24/7",
    qc: "Québec",
    on: "Ontario",
  },
  fr: {
    code: "Erreur 404",
    h1: "Page introuvable",
    lead: "La page demandée n'existe pas ou a été déplacée. Essayez l'un des liens ci-dessous ou appelez-nous : nos lignes répondent 24 h/24, 7 j/7.",
    home: ["/fr", "Aller à l'accueil"],
    contact: ["/fr/contact", "Nous joindre"],
    servicesTitle: "Services",
    services: [
      ["/fr/services/agents-de-securite", "Agents de sécurité"],
      ["/fr/services/securite-evenementielle", "Sécurité événementielle"],
      ["/fr/services/patrouille-mobile", "Patrouille mobile"],
    ],
    citiesTitle: "Villes",
    cities: [
      ["/fr/montreal", "Montréal"],
      ["/fr/toronto", "Toronto"],
      ["/fr/ottawa", "Ottawa"],
      ["/fr/quebec", "Québec"],
      ["/fr/laval", "Laval"],
      ["/fr/trois-rivieres", "Trois-Rivières"],
      ["/fr/niagara-falls", "Niagara Falls"],
    ],
    callTitle: "Appelez-nous, 24/7",
    qc: "Québec",
    on: "Ontario",
  },
};

const S = {
  page: { background: "#0f1225", color: "#e9ebf7", padding: "120px 16px 72px", minHeight: "70vh" },
  wrap: { maxWidth: 1040, margin: "0 auto" },
  code: { fontSize: ".85rem", letterSpacing: ".2em", textTransform: "uppercase", color: "#8f9dff", margin: "0 0 8px" },
  h1: { fontSize: "clamp(1.8rem, 5vw, 2.6rem)", lineHeight: 1.2, margin: "0 0 12px", color: "#fff" },
  lead: { color: "#a9afd1", maxWidth: 640, margin: "0 0 28px", lineHeight: 1.6 },
  actions: { display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 40 },
  primary: { display: "inline-block", padding: "12px 22px", borderRadius: 8, fontWeight: 600, background: "#3244ba", color: "#fff", textDecoration: "none" },
  ghost: { display: "inline-block", padding: "12px 22px", borderRadius: 8, fontWeight: 600, border: "1px solid #262b4d", color: "#e9ebf7", textDecoration: "none" },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16 },
  card: { background: "#161a33", border: "1px solid #262b4d", borderRadius: 12, padding: 20 },
  h2: { fontSize: "1rem", margin: "0 0 10px", color: "#fff" },
  list: { listStyle: "none", margin: 0, padding: 0 },
  item: { padding: "4px 0" },
  link: { color: "#8f9dff", textDecoration: "none" },
};

export default function NotFound({ forceFr }) {
  const { pathname } = useLocation();
  const isFR = forceFr === true || (forceFr !== false && /^\/fr(\/|$)/.test(pathname));
  const t = isFR ? TEXT.fr : TEXT.en;
  const links = (items) => (
    <ul style={S.list}>
      {items.map(([to, label]) => (
        <li key={to} style={S.item}><Link to={to} style={S.link}>{label}</Link></li>
      ))}
    </ul>
  );

  return (
    <main style={S.page}>
      <SEO
        title={t.h1}
        description={t.lead}
        canonical={pathname}
        lang={isFR ? "fr" : "en"}
        alternates={{}}
        noIndex
      />
      <div style={S.wrap}>
        <p style={S.code}>{t.code}</p>
        <h1 style={S.h1}>{t.h1}</h1>
        <p style={S.lead}>{t.lead}</p>
        <div style={S.actions}>
          <Link to={t.home[0]} style={S.primary}>{t.home[1]}</Link>
          <Link to={t.contact[0]} style={S.ghost}>{t.contact[1]}</Link>
        </div>
        <div style={S.grid}>
          <section style={S.card}>
            <h2 style={S.h2}>{t.servicesTitle}</h2>
            {links(t.services)}
          </section>
          <section style={S.card}>
            <h2 style={S.h2}>{t.citiesTitle}</h2>
            {links(t.cities)}
          </section>
          <section style={S.card}>
            <h2 style={S.h2}>{t.callTitle}</h2>
            <ul style={S.list}>
              <li style={S.item}>{t.qc}: <a href="tel:+15142797209" style={S.link}>514-279-7209</a></li>
              <li style={S.item}>{t.on}: <a href="tel:+14165880180" style={S.link}>416-588-0180</a></li>
              <li style={S.item}><Link to={t.contact[0]} style={S.link}>{t.contact[1]}</Link></li>
            </ul>
          </section>
        </div>
      </div>
    </main>
  );
}
